/**
 * Game descriptions come from the Laravel admin's RichEditor as HTML
 * (older games still hold plain text). The admin sanitises on save, but the
 * landing page never trusts stored HTML: it is rebuilt here through a strict
 * allowlist that matches the editor's toolbar, so only formatting survives.
 *
 * Parsing uses DOMParser, which builds an inert document — no scripts run and
 * no resources load while the HTML is inspected.
 */

/** tags the admin editor can produce; anything else is unwrapped to its text */
const ALLOWED_TAGS = new Set([
  'P', 'BR', 'H2', 'H3', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'STRIKE', 'DEL',
  'UL', 'OL', 'LI', 'BLOCKQUOTE', 'A',
]);
/** dropped with their content (never meaningful as text) */
const DROP_WITH_CONTENT = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'IFRAME', 'OBJECT', 'EMBED', 'NOSCRIPT', 'SVG', 'MATH', 'HEAD', 'TITLE']);
const BLOCK_TAGS = new Set(['P', 'H2', 'H3', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'DIV']);
const SAFE_URL = /^(https?:|mailto:)/i;

/** HTML only if it carries a tag the editor (or a typical paste) produces —
 *  so legacy plain text like "a <b> c" or "<tag>" stays literal text */
const looksLikeHtml = (s) => /<\/?(p|br|h[1-6]|strong|b|em|i|u|s|strike|del|ul|ol|li|blockquote|a|div|span)(\s[^>]*)?\/?>/i.test(s);

function escapeHtml(s) {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/** plain text → paragraphs; blank lines split paragraphs, single newlines become <br> */
function plainTextToHtml(text) {
  return text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

function parse(html) {
  return new DOMParser().parseFromString(`<!doctype html><body>${html}</body>`, 'text/html').body;
}

/** copy `src`'s children into `dest` (of `doc`), keeping only allowlisted markup */
function cleanInto(src, dest, doc) {
  for (const node of Array.from(src.childNodes)) {
    if (node.nodeType === 3) {
      dest.appendChild(doc.createTextNode(node.nodeValue));
      continue;
    }
    if (node.nodeType !== 1) continue; // comments, processing instructions…
    const tag = node.tagName.toUpperCase();
    if (DROP_WITH_CONTENT.has(tag)) continue;
    if (!ALLOWED_TAGS.has(tag)) {
      cleanInto(node, dest, doc); // unwrap: keep the text, lose the element
      continue;
    }
    const el = doc.createElement(tag.toLowerCase());
    if (tag === 'A') {
      const href = (node.getAttribute('href') || '').trim();
      if (!SAFE_URL.test(href)) {
        cleanInto(node, dest, doc); // unsafe/empty link → just its text
        continue;
      }
      el.setAttribute('href', href);
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noopener noreferrer nofollow');
    }
    cleanInto(node, el, doc);
    dest.appendChild(el);
  }
}

/** Safe HTML for dangerouslySetInnerHTML: allowlisted formatting only. '' if empty. */
export function sanitizeRichText(input) {
  if (input == null) return '';
  const raw = String(input).trim();
  if (!raw) return '';
  const html = looksLikeHtml(raw) ? raw : plainTextToHtml(raw);
  const src = parse(html);
  const doc = src.ownerDocument;
  const out = doc.createElement('div');
  cleanInto(src, out, doc);
  return out.textContent.trim() ? out.innerHTML : '';
}

/** Readable one-line text for previews: block boundaries become spaces. */
export function richTextToPlain(input) {
  if (input == null) return '';
  const raw = String(input);
  if (!looksLikeHtml(raw)) return raw.replace(/\s+/g, ' ').trim();
  const body = parse(raw);
  const parts = [];
  const walk = (node) => {
    for (const n of Array.from(node.childNodes)) {
      if (n.nodeType === 3) parts.push(n.nodeValue);
      else if (n.nodeType === 1) {
        const tag = n.tagName.toUpperCase();
        if (DROP_WITH_CONTENT.has(tag)) continue;
        if (tag === 'BR') { parts.push(' '); continue; }
        const block = BLOCK_TAGS.has(tag);
        if (block) parts.push(' ');
        walk(n);
        if (block) parts.push(' ');
      }
    }
  };
  walk(body);
  return parts.join('').replace(/\s+/g, ' ').trim();
}
